import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  QUERY_KEY_ALL_SUPERSTARS,
  QUERY_KEY_MY_SUPERSTARS,
  QUERY_KEY_SUPERSTAR_SEARCH_SUGGESTIONS,
  QUERY_KEY_SUPERSTAR_TRENDING,
  QUERY_KEY_SUPERSTAR_ERAS,
} from '@wwe-portal/web/utils/constants/query-key';
import { useGetCurrentUserId } from '../stores/auth-store';
import {
  getAllSuperstars,
  getMySuperstars,
  getMySuperstarById,
  getSuggestions,
  getTrendingSuperstars,
  getEras,
} from '@wwe-portal/web/services/client/superstar';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { MAX_SUPERSTAR_SEARCH_TEXT_LENGTH } from '@wwe-portal/web/utils/constants/superstar';
import { GetSuperstarFilter } from '@wwe-portal/entities/superstar.type';

const getAllFromAPI = async <T>(
  fn: (params: {
    skip: number;
    perPage: number;
  }) => Promise<AxiosResponse<{ data: T[] }>>,
  perPage: number
) => {
  const result: T[] = [];
  let skip = 0;
  let hasMore = true;
  while (hasMore) {
    const response = await fn({ skip, perPage });
    result.push(...response.data.data);
    skip += perPage;
    hasMore = response.data.data.length === perPage;
  }
  return result;
};

const PER_PAGE = 24;

export const useTrendingSuperstars = () => {
  return useQuery({
    queryKey: [QUERY_KEY_SUPERSTAR_TRENDING],
    queryFn: async () => {
      const response = await getTrendingSuperstars();
      return response.data;
    },
  });
};

export const useGetUserSuperstars = (filter?: GetSuperstarFilter) => {
  const userId = useGetCurrentUserId();
  return useQuery({
    queryKey: [QUERY_KEY_MY_SUPERSTARS, { userId, filter }],
    queryFn: async () => {
      return getAllFromAPI(
        (params) =>
          getMySuperstars({
            ...params,
            ...filter,
          }),
        PER_PAGE
      );
    },
    enabled: !!userId,
  });
};

export const useGetMySuperstarById = (id: string) => {
  const userId = useGetCurrentUserId();
  return useQuery({
    queryKey: [QUERY_KEY_MY_SUPERSTARS, { userId, id }],
    queryFn: async () => {
      const response = await getMySuperstarById(id);
      return response.data;
    },
    enabled: !!userId,
  });
};

export const useGetMySuperstarMap = (filter?: GetSuperstarFilter) => {
  const { data: userSuperstars, isLoading } = useGetUserSuperstars(filter);
  const superstarMap = useMemo(
    () =>
      userSuperstars
        ? new Map(
            userSuperstars.map((superstar) => [superstar.groupId, superstar])
          )
        : undefined,
    [userSuperstars]
  );
  return { superstarMap, isLoading };
};

export const useGetAllSuperstarsInfinite = (
  filter: GetSuperstarFilter,
  perPage: number = PER_PAGE
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY_ALL_SUPERSTARS, filter],
    queryFn: async ({ pageParam }) => {
      const response = await getAllSuperstars({
        skip: pageParam * perPage,
        perPage,
        ...filter,
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.data.length === perPage ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });
};

export const useGetSuperstarSearchSuggestions = (searchText: string) => {
  const querySearchText = useMemo(
    () => searchText.trim().slice(0, MAX_SUPERSTAR_SEARCH_TEXT_LENGTH),
    [searchText]
  );
  return useQuery({
    enabled: querySearchText.length > 1,
    queryKey: [QUERY_KEY_SUPERSTAR_SEARCH_SUGGESTIONS, { querySearchText }],
    queryFn: async () => {
      const response = await getSuggestions({
        keyword: querySearchText,
      });
      return response.data;
    },
  });
};

export const useEras = () => {
  return useQuery({
    queryKey: [QUERY_KEY_SUPERSTAR_ERAS],
    queryFn: async () => {
      const response = await getEras();
      return response.data;
    },
  });
};
